import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { Link } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';

import {
  BereikenIcon,
  BottomNav,
  DocIcon,
  ResultBottomNav,
  StepLayout,
  WatHebIkIcon,
} from '@myfaro/ui-react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../core/context';
import Who from './Who';
import What from './What';
import Goals from './Goals';
import Result from './Result';
import ContactDetails from './ContactDetails';
import { useProfileContext } from '../../core/profile';
import { Profile } from '../../core/types';
import { addBaseName } from '../../helpers';

type TParams = { step: string };

function Step({ match, history }: RouteComponentProps<TParams>) {
  const { t } = useTranslation();
  const { appConfig } = useAppContext();
  const { useCurrentStepHasAllFields, useProfile } = useProfileContext();
  const profile = useProfile<Profile>();
  const { params } = match;
  const { step } = params;
  const { currentStepHasAllFields } = useCurrentStepHasAllFields(
    step as keyof Profile,
  );

  const { logoSrc, heroImgSrc } = appConfig;
  // return params?.step ?? '404';

  const defaultBottomNavProps = {
    prevEnabled: step !== 'who',
    prevLabel: t('label.previous'),
    onClickPrev: () => {
      const indexPreviousStep =
        steps.findIndex(stepObj => stepObj.title === step) - 1;
      history.push(`/${steps[indexPreviousStep]?.title || ''}`);
    },
    // nextEnabled: true,
    nextEnabled: step !== 'result' && currentStepHasAllFields,
    nextLabel: t('label.next'),
    onClickNext: () => {
      const indexNextStep =
        steps.findIndex(stepObj => stepObj.title === step) + 1;
      history.push(`/${steps[indexNextStep].title}`);
    },
  };

  const defaultStep = {
    progressBarProps: {
      icon: <PersonIcon fontSize='large' />,
      onClickStep: (index: number) => {
        history.push(`${steps[index].progressBarProps.title}`);
      },
      totalNumberOfSteps: 4,
    },
    navigationHeaderProps: {
      showHeader: true,
      showChildren: true,
      logoSrc: addBaseName(logoSrc),
      children: appConfig.helpNeeded && (
        <Link
          href={'https://www.google.be'}
          rel='noopener noreferrer'
          target='_blank'>
          {'Hulp nodig ?'}
        </Link>
      ),
    },
    bottomNav: <BottomNav {...defaultBottomNavProps} />,
  };

  let steps = [
    {
      ...defaultStep,
      title: 'who',
      component: Who,
      active: true,
      progressBarProps: {
        ...defaultStep.progressBarProps,
        icon: <PersonIcon />,
        text: t('step.who'),
        title: 'who',
        currentStepIndex: 0,
      },
    },
    {
      ...defaultStep,
      title: 'what',
      component: What,
      active: profile.who?.isCompleted,
      progressBarProps: {
        ...defaultStep.progressBarProps,
        icon: <WatHebIkIcon />,
        text: t('step.what'),
        title: 'what',
        currentStepIndex: 1,
      },
    },
    {
      ...defaultStep,
      title: 'goals',
      component: Goals,
      active: profile.what?.isCompleted,
      progressBarProps: {
        ...defaultStep.progressBarProps,
        icon: <BereikenIcon />,
        text: t('step.goals'),
        title: 'goals',
        currentStepIndex: 2,
      },
    },
    {
      ...defaultStep,
      title: 'contact',
      component: ContactDetails,
      active: profile.goals?.isCompleted,
      progressBarProps: {
        ...defaultStep.progressBarProps,
        icon: <DocIcon />,
        text: t('step.result'),
        title: 'contact',
        currentStepIndex: 3,
      },
    },
    {
      ...defaultStep,
      title: 'result',
      component: Result,
      active: !!profile.result,
      progressBarProps: {
        ...defaultStep.progressBarProps,
        icon: <DocIcon />,
        text: t('step.result'),
        title: 'result',
        currentStepIndex: 3,
      },
      bottomNav: (
        <ResultBottomNav
          mainButtonLabel={t('result.editProfile')}
          onClickMain={() => history.push('/who')}
        />
      ),
    },
  ];

  if (profile.result) {
    // take out the contact step once we have the results
    steps = steps.filter(step => step.title !== 'contact');
  }

  const currentStep: any =
    steps.find((stepObj: any) => step === stepObj.title) ?? steps[0];

  return (
    <StepLayout
      progressBarProps={{ ...currentStep.progressBarProps, steps }}
      navigationHeaderProps={{ ...currentStep.navigationHeaderProps }}
      bottomNav={currentStep.bottomNav}>
      <Switch>
        {steps.map((step: any) => (
          <Route
            key={step.title}
            path={`/${step.title}`}
            component={step.component}
            exact
          />
        ))}
      </Switch>
    </StepLayout>
  );
}

export default Step;
